import { FileText, Files, Search } from "lucide-react";

import { LoginPane } from "@/components/shell/LoginPane";
import { Separator } from "@/components/ui/separator";
import type { AccountUser, Accounts } from "@/lib/accounts";

/** The signed-out shell. Deliberately NOT the populated atlas: there is no top
 * bar and no right rail (both are being rebuilt), only an EMPTY file-rail on
 * the left, a crisp white hairline separating it from the atlas area, and the
 * fully-wired login pane centered in the atlas. Everything is drawn from the
 * shadcn/ui obsidian tokens (`--sidebar`, `--background`, `--card`). */
export function SignedOutScreen({
  accs,
  onSignedIn,
}: {
  accs: Accounts;
  onSignedIn?: (user: AccountUser | null) => void;
}) {
  return (
    <div className="flex h-full min-h-0 w-full bg-background text-foreground">
      <EmptyFileRail />

      {/* The requested white line between the file-rail and the atlas area. */}
      <Separator orientation="vertical" className="bg-white/25" />

      {/* Atlas area — hosts the login pane instead of the graph while signed out. */}
      <main className="relative grid min-h-0 flex-1 place-items-center overflow-hidden p-6">
        <LoginPane accs={accs} onSignedIn={onSignedIn} />
      </main>
    </div>
  );
}

/** The left file bar, rebuilt in shadcn (obsidian `--sidebar` surface). It's
 * the only piece of chrome kept on the signed-out screen, and it's empty:
 * files appear once you're signed in. */
function EmptyFileRail() {
  return (
    <aside className="hidden w-[238px] shrink-0 flex-col bg-sidebar text-sidebar-foreground sm:flex">
      <div className="flex h-11 shrink-0 items-center gap-2 px-3">
        <Files className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Files</span>
      </div>

      {/* A disabled, decorative search field to echo the eventual explorer. */}
      <div className="px-3 pb-2">
        <div className="flex h-8 items-center gap-2 rounded-md bg-input px-2.5 text-muted-foreground">
          <Search className="h-3.5 w-3.5" />
          <span className="text-xs">Search files</span>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3">
        <div className="mt-16 flex flex-col items-center gap-3 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-md bg-muted text-muted-foreground">
            <FileText className="h-5 w-5" />
          </div>
          <p className="text-sm text-muted-foreground">No files yet</p>
          <p className="text-xs leading-relaxed text-muted-foreground/70">
            Sign in to open your vault. Your documents and notes will appear here.
          </p>
        </div>
      </div>
    </aside>
  );
}
